import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const EmailTriggerConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['gmail', 'settings']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-3">
      {/* Gmail IMAP Beállítások */}
      <CollapsibleSection
        id="gmail"
        title="Gmail IMAP Kapcsolat"
        icon="📧"
        color="red"
        isExpanded={expandedSections.has('gmail')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Gmail Email Cím
            </label>
            <input
              type="email"
              value={node.data.config.gmailUser || ''}
              onChange={(e) => updateConfig('gmailUser', e.target.value)}
              className={commonStyles.input}
              placeholder="your.email@gmail.com"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              A Gmail fiókod email címe
            </p>
          </div>
          
          <div>
            <label className={commonStyles.label}>
              Gmail App Password
            </label>
            <input
              type="password"
              value={node.data.config.gmailAppPassword || ''}
              onChange={(e) => updateConfig('gmailAppPassword', e.target.value)}
              className={commonStyles.input}
              placeholder="16 karakteres App Password"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Gmail App Password (nem a normál jelszó!) - 
              <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                Hogyan hozd létre?
              </a>
            </p>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
            <div className="flex items-start space-x-2">
              <span className="text-yellow-600 dark:text-yellow-400 text-sm">⚠️</span>
              <div className="text-xs text-yellow-700 dark:text-yellow-300">
                <strong>App Password beállítás:</strong>
                <ol className="list-decimal list-inside mt-1 space-y-1">
                  <li>Google fiók → Biztonság → 2-lépcsős hitelesítés bekapcsolása</li>
                  <li>Alkalmazásjelszavak → Új alkalmazásjelszó generálása</li>
                  <li>16 karakteres kódot másold be ide</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Email Beállítások */}
      <CollapsibleSection
        id="settings"
        title="Email Beállítások"
        icon="⚙️"
        color="blue"
        isExpanded={expandedSections.has('settings')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Postafiók
            </label>
            <select
              value={node.data.config.mailbox || 'inbox'}
              onChange={(e) => updateConfig('mailbox', e.target.value)}
              className={commonStyles.select}
            >
              <option value="inbox">Beérkezett</option>
              <option value="sent">Elküldött</option>
              <option value="drafts">Piszkozatok</option>
              <option value="spam">Spam</option>
            </select>
          </div>
          
          <div>
            <label className={commonStyles.label}>
              Szűrő szabály (opcionális)
            </label>
            <input
              type="text"
              value={node.data.config.filter || ''}
              onChange={(e) => updateConfig('filter', e.target.value)}
              className={commonStyles.input}
              placeholder="subject:fontos OR from:admin@example.com"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Gmail-stílusú szűrő szabályok használhatók
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="markAsRead"
                checked={node.data.config.markAsRead || false}
                onChange={(e) => updateConfig('markAsRead', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="markAsRead" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Automatikus olvasottnak jelölés
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="includeAttachments"
                checked={node.data.config.includeAttachments || false}
                onChange={(e) => updateConfig('includeAttachments', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="includeAttachments" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Mellékletek feldolgozása
              </label>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Elérhető Változók */}
      <CollapsibleSection
        id="variables"
        title="Elérhető Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            Az email trigger után ezek a változók lesznek elérhetők a flow további részében:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{email_sender}}', desc: 'Feladó email címe', example: 'john@example.com' },
              { var: '{{email_sender_name}}', desc: 'Feladó neve', example: 'John Doe' },
              { var: '{{email_subject}}', desc: 'Email tárgya', example: 'Árajánlat kérés' },
              { var: '{{email_body}}', desc: 'Email tartalma', example: 'Kedves Uram...' },
              { var: '{{email_date}}', desc: 'Email dátuma', example: 'Mon, 01 Jan 2024...' },
              { var: '{{email_id}}', desc: 'Email azonosító', example: '12345' },
              { var: '{{email_has_attachments}}', desc: 'Van-e melléklet', example: 'true/false' },
              { var: '{{email_attachments_count}}', desc: 'Mellékletek száma', example: '2' },
              { var: '{{emails_count}}', desc: 'Összes email száma', example: '5' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {item.example}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 mt-3">
            <div className="text-xs text-blue-700 dark:text-blue-300">
              <strong>Flow tervezési tippek:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Használj <strong>Condition</strong> node-ot email szűréshez (pl. tárgy alapján)</li>
                <li>Használj <strong>CRM Action</strong> node-ot ügyfél létrehozáshoz</li>
                <li>Használj <strong>SMTP Email</strong> node-ot automatikus válaszhoz</li>
                <li>Használj <strong>Notification</strong> node-ot értesítésekhez</li>
              </ul>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Előnézet */}
      <CollapsibleSection
        id="preview"
        title="Konfiguráció Előnézet"
        icon="👁️"
        color="gray"
        isExpanded={expandedSections.has('preview')}
        onToggle={toggleSection}
      >
        <div className={commonStyles.codeBlock}>
          <div className="text-green-400 font-mono text-xs space-y-1">
            <div>📧 Gmail: {node.data.config.gmailUser || 'nincs beállítva'}</div>
            <div>🔑 App Password: {node.data.config.gmailAppPassword ? '***beállítva***' : 'nincs beállítva'}</div>
            <div>📁 Postafiók: {node.data.config.mailbox || 'inbox'}</div>
            <div>🔍 Szűrő: {node.data.config.filter || 'nincs'}</div>
            <div>✅ Olvasottnak jelöl: {node.data.config.markAsRead ? 'igen' : 'nem'}</div>
            <div>📎 Mellékletek: {node.data.config.includeAttachments ? 'igen' : 'nem'}</div>
            <div>🔗 Változók: 9 db elérhető a flow-ban</div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default EmailTriggerConfig;
